/* The href guard, alone in its own file with no stylesheet and no React, so
   a file that only cleans a CMS href (Text's tooltip sanitizer) does not pull
   in the Link component and its CSS. link.tsx re-exports both names. */

/* An href is CMS content, so its scheme is untrusted — `javascript:` executes
   in-origin and React never inspects a scheme; anything refused becomes "#".
   The relative pattern is a character set, not a leading-slash test: the URL
   parser deletes tab/LF/CR anywhere in the input and reads `\` as `/`, so
   "/\evil.com" would otherwise resolve to the off-site "//evil.com". */
const SAFE_RELATIVE = /^(?:\/(?!\/)|[#?])[\w\-./~%!$&'()*+,;=:@?#]*$/;
import { LINK_SCHEMES } from "../../services/safe-href";

export { LINK_SCHEMES };
/* Escaped rather than interpolated raw: every entry is a plain scheme today,
   but one carrying a `+` ("svn+ssh:") would silently become a quantifier and
   widen the gate instead of failing. */
const SAFE_SCHEME = new RegExp(
  `^(?:${LINK_SCHEMES.map(scheme => scheme.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
  "i"
);

/**
 * A destination safe to put in an `href`, or `"#"` for anything refused.
 *
 * ⚠ EXPORTED so a component that hands a CMS href to something other than this
 * atom sanitises it with the SAME guard rather than a second copy of these two
 * patterns — a duplicate would drift, and the one that drifted would be the one
 * nobody ran the scheme cases against. WAT-11671: the Grow Card Carousel's
 * `cta_href` goes to `Button`, which renders the value verbatim, so the card
 * sanitises at the point it READS the field.
 *
 * ⚠ It never answers `""`. A caller that distinguishes "no destination" from
 * "a destination" — Button and Link both render a `<button>` without one — must
 * therefore test for an authored value BEFORE calling this, or an unauthored
 * field turns into a link to `"#"`.
 */
export function safeHref(raw: unknown): string {
  /* Total on every input: a feed need not agree with a type, and a security
     guard that throws takes the page down with it. */
  if (typeof raw !== "string") {
    return "#";
  }

  const value = raw.trim();

  if (SAFE_RELATIVE.test(value)) {
    return value;
  }

  return SAFE_SCHEME.test(value) ? value : "#";
}
